<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useInstallPrompt } from '@/composables/useInstallPrompt';

const { t } = useI18n();
const route = useRoute();
const { canInstall, promptInstall, dismiss } = useInstallPrompt();

// Rendered from App.vue's FloatingBannerStack, so without this it also reaches
// the sign-in screens and offers to install an app the visitor cannot use until
// they authenticate. Offer it inside the app instead.
const showBanner = computed(() => canInstall.value && route.meta.public !== true);
</script>

<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div v-if="showBanner" role="dialog" aria-live="polite" class="pointer-events-auto max-w-sm rounded-lg border border-rose-200 bg-white shadow-lg">
      <div class="flex items-start gap-3 p-4">
        <div class="flex-shrink-0 mt-0.5">
          <svg class="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-900">
            {{ t('pwa.install.title') }}
          </p>
          <p class="mt-1 text-sm text-gray-600">
            {{ t('pwa.install.description') }}
          </p>
          <div class="mt-3 flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium rounded-md transition-colors"
              @click="promptInstall"
            >
              {{ t('pwa.install.installNow') }}
            </button>
            <button
              type="button"
              data-test="install-dismiss"
              class="inline-flex items-center px-3 py-1.5 text-gray-600 hover:text-gray-800 text-sm font-medium rounded-md transition-colors"
              @click="dismiss"
            >
              {{ t('pwa.install.dismiss') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
